import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  Clock3,
  ClipboardCheck,
  LayoutDashboard,
  Loader2,
  LogOut,
  Menu as MenuIcon,
  QrCode,
  RefreshCw,
  StickyNote,
  Umbrella,
  X,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { useEffectiveMe } from '../lib/effectiveMe';
import { useSelfEmployee } from '../lib/selfIdentity';
import {
  canManageOwnAvailability,
  canSelfAttendance,
  canViewTurni,
  hasCap,
} from '../lib/capabilities';
import {
  apiFetch,
  supabase,
} from '../lib/supabaseClient';
import { formatAttendanceHours as formatHours } from '../lib/attendanceHours';
import { submitNativeNfcAttendance } from '../lib/nfcNativeChannel';
import HoursPdfExport from '../components/HoursPdfExport';
import AttendanceQrCheckin from '../components/AttendanceQrCheckin';
import HaccpTemperatureShortcut from '../features/haccp/HaccpTemperatureShortcut';
import { cn } from '../lib/utils';

const localDateIso = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Rome',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const part = (type) => parts.find((item) => item.type === type)?.value || '';
  return `${part('year')}-${part('month')}-${part('day')}`;
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const formatDate = (value, options = {}) => {
  if (!value) return '—';
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('it-IT', {
    day: 'numeric',
    month: 'long',
    ...options,
  }).format(date);
};

const formatTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    const match = String(value).match(/(\d{2}):(\d{2})/);
    return match ? `${match[1]}:${match[2]}` : '—';
  }
  return new Intl.DateTimeFormat('it-IT', {
    timeZone: 'Europe/Rome',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const day = new Intl.DateTimeFormat('it-IT', {
    timeZone: 'Europe/Rome',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
  const time = new Intl.DateTimeFormat('it-IT', {
    timeZone: 'Europe/Rome',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
  return `${day} ${time}`;
};

const getName = (me, self) => (
  self?.displayName
  || [self?.nome, self?.cognome].filter(Boolean).join(' ')
  || me?.full_name
  || me?.name
  || me?.email
  || 'Dipendente'
);

const serviceName = (shift) => (
  shift?.service?.name
  || shift?.service?.code
  || shift?.service_name
  || 'Turno di lavoro'
);

function PublishedShiftNote({ shift, testId }) {
  const note = String(shift?.notes || '').trim();
  if (!note) return null;
  return (
    <div className="mt-2 flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-900"
      data-testid={testId}>
      <StickyNote className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span className="whitespace-pre-wrap break-words">{note}</span>
    </div>
  );
}

const isPublishedRest = (shift) => String(shift?.shift_type || '').toUpperCase() === 'RIPOSO';

function Card({ children, className = '', testId, id }) {
  return (
    <section
      id={id}
      className={cn('rounded-2xl border border-stone-200 bg-white p-4 shadow-sm md:p-5', className)}
      data-testid={testId}
    >
      {children}
    </section>
  );
}

function CardHeading({ icon: Icon, title, to, actionLabel = 'Vedi tutto' }) {
  return (
    <div className="mb-4 flex items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-2">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-green/10 text-brand-green">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
        <h2 className="truncate font-display text-lg font-semibold text-brand-navy">{title}</h2>
      </div>
      {to && (
        <Link className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-brand-green hover:text-brand-green-hover" to={to}>
          {actionLabel}<ChevronRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      )}
    </div>
  );
}

function EmptyState({ title, detail, icon: Icon = CalendarDays, testId }) {
  return (
    <div className="rounded-xl border border-dashed border-stone-200 bg-stone-50/70 px-4 py-6 text-center" data-testid={testId}>
      <Icon className="mx-auto h-7 w-7 text-stone-300" aria-hidden="true" />
      <p className="mt-2 text-sm font-semibold text-brand-navy">{title}</p>
      {detail && <p className="mt-1 text-xs leading-5 text-stone-500">{detail}</p>}
    </div>
  );
}

const attendanceLocation = (record) => (
  record?.location_name_snapshot || record?.location_name || null
);

function isValidAttendanceOverview(value) {
  if (!value || typeof value !== 'object') return false;
  const date = value.today;
  const parsedDate = typeof date === 'string' ? new Date(`${date}T00:00:00Z`) : null;
  return Boolean(
    parsedDate && !Number.isNaN(parsedDate.getTime()) && parsedDate.toISOString().slice(0, 10) === date
    && value.timezone === 'Europe/Rome'
    && typeof value.total_seconds_today === 'number'
    && Number.isFinite(value.total_seconds_today) && value.total_seconds_today >= 0
    && (value.open_presence === null || (value.open_presence && typeof value.open_presence === 'object'))
    && (value.last_event === null || (
      value.last_event && ['ENTRY', 'EXIT'].includes(value.last_event.event_type)
      && typeof value.last_event.occurred_at === 'string'
      && typeof value.last_event.location_name === 'string'
    ))
    && Array.isArray(value.recent)
    && Array.isArray(value.anomalies)
  );
}

export function PresenceCard({
  onScan,
  overview,
  totalSecondsToday,
  anomalies = [],
  loading = false,
  actionError,
  actionNotice,
}) {
  const open = overview?.open_presence || null;
  const event = overview?.last_event || null;
  const inService = Boolean(open);
  const latestLabel = event?.event_type === 'EXIT' ? 'Ultima uscita' : event?.event_type === 'ENTRY' ? 'Ultima entrata' : 'Ultimo evento';
  const visibleAnomalies = anomalies.filter((item, index) => (
    !(open && /^presenza aperta\b/i.test(String(item)))
    && anomalies.indexOf(item) === index
  ));

  return (
    <Card className="overflow-hidden border-brand-navy bg-brand-navy p-0 text-white" testId="employee-presence-card">
      <div className="p-4 md:p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-white/65">
              <Clock3 className="h-4 w-4" aria-hidden="true" data-testid="employee-presence-icon" /> Presenza di oggi
            </div>
            <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight" data-testid="employee-presence-status">
              {inService ? 'Al lavoro' : 'Fuori servizio'}
            </h2>
            {open && <p className="mt-1 text-sm text-white/75">Entrata {formatTime(open.entered_at_utc)} · {attendanceLocation(open) || 'Locale non indicato'}</p>}
          </div>
          <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${inService ? 'bg-emerald-300/20 text-emerald-200' : 'bg-white/10 text-white/70'}`}>
            {inService ? <CheckCircle2 className="h-5 w-5" aria-hidden="true" /> : <Clock3 className="h-5 w-5" aria-hidden="true" />}
          </span>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2 border-t border-white/10 pt-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-white/55">{latestLabel}</p>
            <p className="mt-0.5 text-sm font-semibold">{event ? formatTime(event.occurred_at) : 'Nessuna registrazione'}</p>
            {event?.location_name && <p className="truncate text-xs text-white/65">{event.location_name}</p>}
          </div>
          <div className="text-right">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-white/55">Ore di oggi</p>
            <p className="mt-0.5 font-display text-xl font-semibold" data-testid="employee-hours-today">{formatHours(totalSecondsToday)}</p>
            <p className="text-[10px] text-white/55">giornata locale Europe/Rome</p>
          </div>
        </div>

        {open && (
          <p className="mt-3 rounded-lg border border-amber-200/20 bg-amber-300/10 px-3 py-2 text-xs text-amber-50" data-testid="employee-open-presence-notice">
            Presenza aperta: le ore saranno calcolate dopo l&apos;uscita.
          </p>
        )}
        {visibleAnomalies.map((item, index) => (
          <p key={`${index}-${item}`} className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200/20 bg-amber-300/10 px-3 py-2 text-xs text-amber-50" data-testid="employee-attendance-anomaly">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />{item}
          </p>
        ))}

        {actionError && <div className="mt-3 rounded-lg border border-red-200/30 bg-red-500/15 px-3 py-2 text-xs text-red-100" role="alert">{actionError}</div>}
        {actionNotice && <div className="mt-3 rounded-lg border border-emerald-200/30 bg-emerald-500/15 px-3 py-2 text-xs text-emerald-50" role="status">{actionNotice}</div>}
      </div>
    </Card>
  );
}

export default function EmployeeDashboard() {
  const { me: realMe, signOut } = useAuth() || {};
  const effectiveMe = useEffectiveMe();
  const { self, isSimulated } = useSelfEmployee();
  const {
    hydrated,
    hydrationError,
    reloadDataset,
    periodi = [],
    saldoOf,
  } = useApp() || {};
  const [personalData, setPersonalData] = useState({
    shifts: [], attendance: [], overview: null, loading: true, error: '', attendanceError: '',
  });
  const [selectedAttendanceMonth, setSelectedAttendanceMonth] = useState(() => localDateIso().slice(0, 7));
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionNotice, setActionNotice] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [leaveRetrying, setLeaveRetrying] = useState(false);
  const retryLeaveDataset = async () => {
    setLeaveRetrying(true);
    try {
      await reloadDataset();
    } catch {
      // AppContext retains the error; retry must not leave an unhandled rejection.
    } finally {
      setLeaveRetrying(false);
    }
  };

  const fallbackToday = useMemo(() => localDateIso(), []);
  const today = personalData.overview?.today || fallbackToday;
  const selectedMonthLabel = useMemo(() => {
    const date = new Date(`${selectedAttendanceMonth}-01T00:00:00Z`);
    if (Number.isNaN(date.getTime())) return selectedAttendanceMonth;
    return new Intl.DateTimeFormat('it-IT', {
      timeZone: 'UTC',
      month: 'long',
      year: 'numeric',
    }).format(date);
  }, [selectedAttendanceMonth]);
  const selectedMonthAttendance = useMemo(() => personalData.attendance
    .filter((record) => String(record?.local_date || '').slice(0, 7) === selectedAttendanceMonth)
    .slice()
    .sort((left, right) => String(right.entered_at_utc || '').localeCompare(String(left.entered_at_utc || ''))),
  [personalData.attendance, selectedAttendanceMonth]);
  const officialMonthAttendance = selectedMonthAttendance.filter((record) => (
    record?.status === 'CHIUSA'
    && record.hours_seconds !== null
    && record.hours_seconds !== undefined
    && Number.isFinite(Number(record.hours_seconds))
    && Number(record.hours_seconds) >= 0
  ));
  const selectedMonthSeconds = officialMonthAttendance.reduce(
    (total, record) => total + Number(record.hours_seconds), 0,
  );
  const latestMonthShift = officialMonthAttendance[0] || null;
  const fromDate = today;
  const toDate = useMemo(() => localDateIso(addDays(new Date(`${today}T12:00:00`), 45)), [today]);
  const displayName = getName(effectiveMe || realMe, self);
  const canAccessTurni = canViewTurni(effectiveMe) || canManageOwnAvailability(effectiveMe);
  const canAccessAttendance = canSelfAttendance(effectiveMe);
  const canAccessFerie = hasCap(effectiveMe, 'ferie.view') || hasCap(effectiveMe, 'ferie.request');
  const canAccessDocuments = hasCap(effectiveMe, 'self.view');

  const handleLogout = async () => {
    await signOut();
    window.location.assign('/');
  };

  const loadPersonalData = useCallback(async () => {
    setPersonalData((current) => ({ ...current, loading: true, error: '', attendanceError: '' }));
    if (isSimulated) {
      setPersonalData({ shifts: [], attendance: [], overview: null, loading: false, error: '', attendanceError: '' });
      return;
    }
    const results = await Promise.allSettled([
        canAccessTurni
          ? apiFetch(`/api/turni/my-shifts?from_date=${fromDate}&to_date=${toDate}`)
          : Promise.resolve([]),
        canAccessAttendance ? apiFetch('/api/attendance/me') : Promise.resolve([]),
        canAccessAttendance ? apiFetch('/api/attendance/me/overview') : Promise.resolve(null),
    ]);
    const [shiftsResult, attendanceResult, overviewResult] = results;
    const overviewValid = !canAccessAttendance
      || (overviewResult.status === 'fulfilled' && isValidAttendanceOverview(overviewResult.value));
    const errors = [];
    if (shiftsResult.status === 'rejected') errors.push(shiftsResult.reason?.message || 'Turni non disponibili.');
    if (attendanceResult.status === 'rejected') errors.push(attendanceResult.reason?.message || 'Storico presenze non disponibile.');
    const attendanceError = attendanceResult.status === 'rejected'
      ? (attendanceResult.reason?.message || 'Storico presenze non disponibile.')
      : '';
    if (!overviewValid) {
      errors.push(overviewResult.status === 'rejected'
        ? (overviewResult.reason?.message || 'Riepilogo presenze non disponibile.')
        : 'Riepilogo presenze non disponibile o non valido.');
    }
    setPersonalData((current) => ({
      ...current,
      shifts: shiftsResult.status === 'fulfilled' && Array.isArray(shiftsResult.value)
        ? shiftsResult.value : current.shifts,
      attendance: attendanceResult.status === 'fulfilled' && Array.isArray(attendanceResult.value)
        ? attendanceResult.value : current.attendance,
      overview: overviewValid && canAccessAttendance ? overviewResult.value : current.overview,
      loading: false,
      error: errors.join(' '),
      attendanceError,
    }));
  }, [canAccessAttendance, canAccessTurni, fromDate, isSimulated, toDate]);

  useEffect(() => {
    loadPersonalData();
  }, [loadPersonalData]);

  useEffect(() => {
    const revalidate = () => { if (document.visibilityState === 'visible') loadPersonalData(); };
    document.addEventListener('visibilitychange', revalidate);
    window.addEventListener('focus', revalidate);
    return () => {
      document.removeEventListener('visibilitychange', revalidate);
      window.removeEventListener('focus', revalidate);
    };
  }, [loadPersonalData]);

  const todayShift = useMemo(() => (
    personalData.shifts
      .filter((shift) => (
        shift.shift_date === today
        && shift.status !== 'CANCELLED'
        && !isPublishedRest(shift)
      ))
      .sort((left, right) => String(left.start_time).localeCompare(String(right.start_time)))[0] || null
  ), [personalData.shifts, today]);

  const todayRest = useMemo(() => (
    personalData.shifts
      .filter((shift) => (
        shift.shift_date === today
        && shift.status !== 'CANCELLED'
        && isPublishedRest(shift)
      ))
      .sort((left, right) => String(left.start_time).localeCompare(String(right.start_time)))[0] || null
  ), [personalData.shifts, today]);

  const nextShift = useMemo(() => (
    personalData.shifts
      .filter((shift) => (
        shift.shift_date > today
        && shift.status !== 'CANCELLED'
        && !isPublishedRest(shift)
      ))
      .sort((left, right) => (
        `${left.shift_date}T${left.start_time}`.localeCompare(`${right.shift_date}T${right.start_time}`)
      ))[0] || null
  ), [personalData.shifts, today]);

  const leaveBalance = useMemo(() => {
    if (!hydrated || !self || typeof saldoOf !== 'function') return null;
    return saldoOf(self.id) || null;
  }, [hydrated, saldoOf, self]);
  const availableWholeDays = Math.trunc(leaveBalance?.disponibile ?? 0);

  const currentLeaveRequests = useMemo(() => {
    if (!hydrated || !self) return [];
    return periodi.filter((item) => (
      item.dipendenteId === self.id
      && ['RICHIESTA', 'APPROVATA', 'IN_ATTESA'].includes(String(item.stato || '').toUpperCase())
    ));
  }, [hydrated, periodi, self]);

  const handleNfcScan = async () => {
    if (isSimulated) return;
    if (actionLoading) return;
    setActionLoading(true);
    setActionError('');
    setActionNotice('');
    try {
      const { data: { session } = {} } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw Object.assign(new Error('Sessione scaduta. Accedi nuovamente.'), { status: 401 });
      }
      const nativeOptions = { accessToken: session.access_token };
      if (effectiveMe?.is_global && (effectiveMe?.role_codes || []).includes('CEO')) {
        const context = await apiFetch('/api/ceo/tenant-context');
        if (!context?.active_tenant_id) throw new Error('Seleziona un tenant attivo prima di usare la presenza.');
        nativeOptions.tenantContext = context.active_tenant_id;
      }
      const result = await submitNativeNfcAttendance(nativeOptions);
      const notice = result.outcome === 'DA_VALIDARE'
        ? 'Timbratura acquisita e inviata per verifica.'
        : result.eventType === 'ENTRY'
          ? 'Entrata registrata.'
          : result.eventType === 'EXIT'
            ? 'Uscita registrata.'
            : 'Timbratura acquisita e da verificare.';
      setActionNotice(notice);
      await loadPersonalData();
    } catch (error) {
      if (error?.status !== 401) await loadPersonalData();
      setActionError(error?.status === 401
        ? 'Sessione scaduta. Accedi nuovamente.'
        : 'Esito non confermato. Controlla il riepilogo prima di riprovare.');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="employee-dashboard-shell min-h-screen bg-app-background" data-testid="employee-dashboard">
      <header className="employee-safe-topbar sticky top-0 z-20 border-b border-stone-200/80 px-3 py-3 md:static md:border-0 md:px-0 md:pt-0">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-brand-green text-white shadow-sm">
            <LayoutDashboard className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-green">Area personale</p>
            <h1 className="truncate font-display text-xl font-semibold text-brand-navy">La mia giornata</h1>
          </div>
          <button type="button" onClick={() => setMenuOpen(true)} className="flex h-10 w-10 items-center justify-center rounded-xl border border-stone-200 bg-white text-stone-600 md:hidden" aria-label="Apri menu">
            <MenuIcon className="h-5 w-5" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={handleLogout}
            className="hidden h-10 items-center gap-2 rounded-xl border border-stone-200 bg-white px-3 text-sm font-semibold text-stone-600 hover:bg-stone-50 md:flex"
            data-testid="employee-logout-desktop"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" /> Esci
          </button>
        </div>
      </header>

      <main className="mx-auto flex max-w-5xl flex-col gap-4 px-3 pt-4 md:gap-5 md:px-0 md:pt-5">
        <section className="rounded-2xl border border-brand-green/15 bg-white px-4 py-5 shadow-sm md:px-6" data-testid="employee-greeting">
          <p className="text-sm text-stone-500">{formatDate(today, { weekday: 'long', year: 'numeric' })}</p>
          <h2 className="mt-1 font-display text-2xl font-semibold tracking-tight text-brand-navy md:text-3xl">
            Buongiorno, {displayName}
          </h2>
          <p className="mt-1 text-sm text-stone-500">Qui trovi solo le informazioni della tua giornata.</p>
        </section>

        <HaccpTemperatureShortcut disabled={isSimulated} />

        {isSimulated && (
          <div
            className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900"
            data-testid="employee-dashboard-simulation-notice"
            role="status"
          >
            Modalità simulazione: i dati personali reali e le operazioni di presenza non vengono caricati.
          </div>
        )}

        {personalData.error && (
          <div className="flex items-start justify-between gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900" role="alert">
            <span>{personalData.error}</span>
            <button type="button" onClick={loadPersonalData} className="inline-flex shrink-0 items-center gap-1 font-semibold hover:underline">
              <RefreshCw className="h-4 w-4" aria-hidden="true" /> Riprova
            </button>
          </div>
        )}

        {personalData.loading ? (
          <div className="flex min-h-40 items-center justify-center rounded-2xl border border-stone-200 bg-white text-sm text-stone-500" role="status" data-testid="employee-dashboard-loading">
            <Loader2 className="mr-2 h-5 w-5 animate-spin text-brand-green" aria-hidden="true" /> Caricamento dei tuoi dati…
          </div>
        ) : (
          <>
            {canAccessAttendance && !isSimulated && (
              <section className="flex flex-col gap-3 rounded-2xl border border-brand-green/20 bg-emerald-50 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="employee-qr-attendance-entry">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-brand-green shadow-sm">
                    <QrCode className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <div>
                    <h2 className="text-sm font-semibold text-brand-navy">Timbratura QR</h2>
                    <p className="mt-1 text-xs text-stone-600">Scansiona il codice dinamico del locale. Serve una connessione internet.</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setQrDialogOpen(true)}
                  className="min-h-11 shrink-0 rounded-xl bg-brand-green px-4 text-sm font-semibold text-white"
                  data-testid="employee-qr-attendance-open"
                >
                  Timbratura QR
                </button>
              </section>
            )}
            {canAccessAttendance && !isSimulated && personalData.overview && (
              <PresenceCard
                onScan={handleNfcScan}
                overview={personalData.overview}
                totalSecondsToday={personalData.overview.total_seconds_today}
                anomalies={personalData.overview.anomalies}
                loading={actionLoading}
                actionError={actionError}
                actionNotice={actionNotice}
              />
            )}
            {canAccessAttendance && !isSimulated && !personalData.overview && (
              <Card testId="employee-presence-unavailable" className="border-amber-200 bg-amber-50">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-sm font-semibold text-brand-navy">Presenza non disponibile</h2>
                    <p className="mt-1 text-xs text-stone-600">Lo stato e le ore ufficiali di oggi non sono al momento disponibili.</p>
                  </div>
                  <button type="button" onClick={loadPersonalData} disabled={personalData.loading} className="inline-flex min-h-10 shrink-0 items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 text-xs font-semibold text-brand-navy disabled:opacity-60" data-testid="employee-overview-retry">
                    <RefreshCw className={`h-3.5 w-3.5 ${personalData.loading ? 'animate-spin' : ''}`} aria-hidden="true" /> Riprova
                  </button>
                </div>
              </Card>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              {canAccessTurni && <Card testId="employee-next-shift">
                <CardHeading
                  icon={CalendarDays}
                  title={todayRest ? 'Oggi e prossimo turno' : 'Prossimo turno'}
                  to="/turni"
                />
                {todayShift ? (
                  <div className="rounded-xl border border-brand-green/20 bg-brand-green/5 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-brand-green">Oggi</p>
                    <p className="mt-1 font-display text-xl font-semibold text-brand-navy">{serviceName(todayShift)}</p>
                    <p className="mt-1 text-lg font-semibold text-stone-700">
                      {formatTime(todayShift.start_time)} – {formatTime(todayShift.end_time)}
                    </p>
                    <PublishedShiftNote shift={todayShift} testId="employee-today-shift-note" />
                  </div>
                ) : todayRest ? (
                  <div className="space-y-3">
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="employee-today-rest">
                      <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Oggi</p>
                      <p className="mt-1 font-display text-xl font-semibold text-emerald-950">Riposo</p>
                      <p className="mt-1 text-sm font-semibold text-emerald-800">Pubblicato</p>
                    </div>
                    {nextShift && (
                      <div className="rounded-xl border border-stone-200 bg-stone-50/70 p-4" data-testid="employee-next-work-shift">
                        <p className="text-xs font-semibold uppercase tracking-wider text-brand-green">Prossimo turno</p>
                        <p className="mt-1 font-display text-xl font-semibold text-brand-navy">{serviceName(nextShift)}</p>
                        <p className="mt-1 text-sm font-semibold text-stone-500">{formatDate(nextShift.shift_date)}</p>
                        <p className="mt-1 text-lg font-semibold text-stone-700">
                          {formatTime(nextShift.start_time)} – {formatTime(nextShift.end_time)}
                        </p>
                        <PublishedShiftNote shift={nextShift} testId="employee-next-shift-note" />
                      </div>
                    )}
                  </div>
                ) : nextShift ? (
                  <div className="rounded-xl border border-stone-200 bg-stone-50/70 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-brand-green">{formatDate(nextShift.shift_date)}</p>
                    <p className="mt-1 font-display text-xl font-semibold text-brand-navy">{serviceName(nextShift)}</p>
                    <p className="mt-1 text-lg font-semibold text-stone-700">
                      {formatTime(nextShift.start_time)} – {formatTime(nextShift.end_time)}
                    </p>
                    <PublishedShiftNote shift={nextShift} testId="employee-next-shift-note" />
                  </div>
                ) : (
                  <EmptyState
                    title="Nessun turno in programma"
                    detail="Non risultano turni pubblicati nei prossimi giorni."
                  />
                )}
              </Card>}

              {canAccessFerie && <Card testId="employee-leave-card">
                <CardHeading icon={Umbrella} title="Le mie ferie" to="/ferie-mie" />
                {hydrationError ? (
                  <div role="alert">
                    <EmptyState title="Dati ferie non disponibili" detail="Non è stato possibile caricare i dati. Puoi riprovare." icon={AlertTriangle} />
                    <button type="button" className="mt-3 min-h-11 rounded-xl border px-4 text-sm font-semibold" disabled={leaveRetrying} onClick={retryLeaveDataset}>
                      {leaveRetrying ? 'Caricamento…' : 'Riprova'}
                    </button>
                  </div>
                ) : !hydrated ? (
                  <EmptyState title="Dati ferie in caricamento" detail="Il saldo sarà mostrato appena il dataset personale sarà disponibile." icon={Loader2} />
                ) : leaveBalance ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-xl bg-brand-green/10 p-4">
                      <p className="text-2xl font-display font-semibold text-brand-navy">{availableWholeDays.toLocaleString('it-IT', { useGrouping: true })} {availableWholeDays === 1 ? 'giorno' : 'giorni'}</p>
                      <p className="mt-1 text-xs font-semibold text-stone-500">Disponibili</p>
                    </div>
                    <div className="rounded-xl bg-stone-100 p-4">
                      <p className="text-2xl font-display font-semibold text-brand-navy">{currentLeaveRequests.length}</p>
                      <p className="mt-1 text-xs font-semibold text-stone-500">Richieste</p>
                    </div>
                  </div>
                ) : (
                  <EmptyState title="Saldo ferie non disponibile" detail={hydrationError || 'Non risultano dati ferie personali.'} icon={Umbrella} />
                )}
              </Card>}
            </div>

            {canAccessAttendance && <Card testId="employee-hours-card" id="presenze" className="scroll-mt-24">
              <CardHeading icon={Clock3} title="Le mie ore" to="/presenze" actionLabel="Vedi presenze" />
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-xl bg-stone-50 p-3">
                  <p className="text-xs font-semibold text-stone-500">Totale del mese selezionato</p>
                  <p className="mt-1 font-display text-2xl font-semibold text-brand-navy" data-testid="employee-hours-month-total">
                    {personalData.attendanceError ? '—' : formatHours(selectedMonthSeconds)}
                  </p>
                  <p className="mt-0.5 text-xs capitalize text-stone-500">{selectedMonthLabel}</p>
                </div>
                <div className="rounded-xl bg-stone-50 p-3">
                  <p className="text-xs font-semibold text-stone-500">Ore di oggi</p>
                  <p className="mt-1 font-display text-2xl font-semibold text-brand-navy" data-testid="employee-hours-today-summary">
                    {personalData.overview ? formatHours(personalData.overview.total_seconds_today) : '—'}
                  </p>
                  <p className="mt-0.5 text-xs text-stone-500">Data lavorativa · Europe/Rome</p>
                </div>
              </div>
              {latestMonthShift ? (
                <div className="mt-3 rounded-xl border border-stone-100 px-3 py-2.5" data-testid="employee-hours-last-shift">
                  <p className="text-xs font-semibold text-stone-500">Ultimo turno</p>
                  <p className="mt-1 text-sm font-medium leading-5 text-brand-navy">
                    {formatDateTime(latestMonthShift.entered_at_utc)} – {formatDateTime(latestMonthShift.exited_at_utc)}
                    <span className="font-semibold"> · {formatHours(latestMonthShift.hours_seconds)}</span>
                  </p>
                  <p className="mt-0.5 text-xs text-stone-500" data-testid="employee-hours-last-shift-location">
                    {[latestMonthShift.company_name_snapshot, latestMonthShift.location_name_snapshot].filter(Boolean).join(' · ') || 'Locale non indicato'}
                  </p>
                </div>
              ) : (
                <p className="mt-3 text-sm text-stone-500" data-testid="employee-hours-last-shift-empty">
                  {personalData.attendanceError ? 'Ultimo turno non disponibile.' : 'Nessun turno chiuso nel mese selezionato.'}
                </p>
              )}
              <HoursPdfExport
                mode="self"
                isSimulated={isSimulated}
                testId="employee-hours-pdf"
                selectedMonth={selectedAttendanceMonth}
                onSelectedMonthChange={setSelectedAttendanceMonth}
              />
            </Card>}
          </>
        )}
      </main>

      {qrDialogOpen && (
        <AttendanceQrCheckin
          onClose={() => setQrDialogOpen(false)}
          onCompleted={loadPersonalData}
        />
      )}

      <nav
        className="fixed inset-x-0 bottom-0 z-30 border-t border-stone-200 bg-white/95 px-1 pb-[env(safe-area-inset-bottom)] pt-2 shadow-[0_-4px_16px_rgba(15,23,42,0.06)] backdrop-blur md:static md:mx-auto md:mt-5 md:max-w-5xl md:rounded-2xl md:border md:shadow-sm"
        style={{ display: 'grid', gridTemplateColumns: `repeat(${1 + (canAccessTurni ? 1 : 0) + (canAccessAttendance ? 1 : 0) + (canAccessFerie ? 1 : 0) + 1}, minmax(0, 1fr))` }}
        aria-label="Navigazione personale"
        data-testid="employee-bottom-nav"
      >
        <Link className="flex min-h-12 flex-col items-center justify-center gap-1 rounded-xl text-[11px] font-semibold text-brand-green" to="/foundation" aria-current="page">
          <LayoutDashboard className="h-5 w-5" aria-hidden="true" /><span>Dashboard</span>
        </Link>
        {canAccessTurni && (
          <Link className="flex min-h-12 flex-col items-center justify-center gap-1 rounded-xl text-[11px] font-semibold text-stone-500 hover:bg-stone-50" to="/turni">
            <CalendarDays className="h-5 w-5" aria-hidden="true" /><span>Turni</span>
          </Link>
        )}
        {canAccessAttendance && (
          <Link className="flex min-h-12 flex-col items-center justify-center gap-1 rounded-xl text-[11px] font-semibold text-stone-500 hover:bg-stone-50" to="/presenze">
            <ClipboardCheck className="h-5 w-5" aria-hidden="true" /><span>Presenze</span>
          </Link>
        )}
        {canAccessFerie && (
          <Link className="flex min-h-12 flex-col items-center justify-center gap-1 rounded-xl text-[11px] font-semibold text-stone-500 hover:bg-stone-50" to="/ferie-mie">
            <Umbrella className="h-5 w-5" aria-hidden="true" /><span>Ferie</span>
          </Link>
        )}
        <button type="button" onClick={() => setMenuOpen(true)} className="flex min-h-12 flex-col items-center justify-center gap-1 rounded-xl text-[11px] font-semibold text-stone-500 hover:bg-stone-50">
          <MenuIcon className="h-5 w-5" aria-hidden="true" /><span>Menu</span>
        </button>
      </nav>

      {menuOpen && (
        <>
          <button type="button" className="fixed inset-0 z-40 bg-brand-navy/30 md:hidden" aria-label="Chiudi menu" onClick={() => setMenuOpen(false)} />
          <aside
            className="employee-menu-safe-area fixed inset-x-3 z-50 rounded-2xl border border-stone-200 bg-white p-4 shadow-xl md:hidden"
            role="dialog"
            aria-modal="true"
            aria-label="Menu personale"
            data-testid="employee-menu"
          >
            <div className="flex items-start justify-between gap-3 border-b border-stone-100 pb-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-green">Menu personale</p>
                <h2 className="mt-1 font-display text-lg font-semibold text-brand-navy">Account e documenti</h2>
              </div>
              <button type="button" onClick={() => setMenuOpen(false)} className="flex h-9 w-9 items-center justify-center rounded-xl border border-stone-200 text-stone-500" aria-label="Chiudi menu">
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            <div className="mt-3 grid gap-2">
              {canAccessDocuments && (
                <Link className="flex min-h-11 items-center justify-between rounded-xl bg-stone-50 px-3 text-sm font-semibold text-brand-navy" to="/miei-documenti" onClick={() => setMenuOpen(false)}>
                  Documenti <ChevronRight className="h-4 w-4 text-stone-400" aria-hidden="true" />
                </Link>
              )}
              {canAccessFerie && (
                <Link className="flex min-h-11 items-center justify-between rounded-xl bg-stone-50 px-3 text-sm font-semibold text-brand-navy" to="/ferie-mie" onClick={() => setMenuOpen(false)}>
                  Le mie ferie <ChevronRight className="h-4 w-4 text-stone-400" aria-hidden="true" />
                </Link>
              )}
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex min-h-11 items-center gap-2 rounded-xl bg-red-50 px-3 text-left text-sm font-semibold text-red-800"
                  data-testid="employee-logout"
                >
                  <LogOut className="h-4 w-4" aria-hidden="true" /> Esci
                </button>
            </div>
          </aside>
        </>
      )}
    </div>
  );
}